import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, ExternalLink, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const KpiCard = ({ icon: Icon, couleur, titre, valeur, sub, testId, children, onClick }) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} className={`idc-card p-4 flex gap-3.5 text-left w-full ${onClick ? "hover:shadow-md transition-shadow cursor-pointer" : ""}`} data-testid={testId}>
      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: `${couleur}18` }}>
        <Icon className="w-5 h-5" style={{ color: couleur }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-[--idc-muted] truncate">{titre}</div>
        <div className="text-[15px] sm:text-[21px] font-extrabold leading-tight break-words">{valeur}</div>
        {sub && <div className="text-[11.5px] text-[--idc-muted] mt-0.5">{sub}</div>}
        {children}
      </div>
      {onClick && <ChevronRight className="w-4 h-4 text-[--idc-muted] self-center shrink-0" />}
    </Tag>
  );
};

export const DetailModal = ({ detail, onClose }) => {
  const navigate = useNavigate();
  if (!detail) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dash-detail-modal">
        <DialogHeader>
          <DialogTitle data-testid="dash-detail-titre">{detail.titre}</DialogTitle>
          {detail.sous_titre && <DialogDescription>{detail.sous_titre}</DialogDescription>}
        </DialogHeader>
        {detail.lignes.length === 0 ? (
          <p className="text-[13px] text-[--idc-muted] py-2" data-testid="dash-detail-vide">{detail.vide}</p>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1">
            <table className="w-full text-[12.5px]">
              <thead><tr className="text-[11px] text-[--idc-muted] text-left border-b border-[--idc-border]">
                {detail.colonnes.map((c) => <th key={c} className="py-2 pr-3">{c}</th>)}
              </tr></thead>
              <tbody>
                {detail.lignes.map((l, i) => (
                  <tr key={i} className="border-b border-[--idc-border] last:border-0 hover:bg-[#F1F4F9] cursor-pointer"
                    onClick={() => { onClose(); navigate(l.lien); }} data-testid={`dash-detail-ligne-${i}`}>
                    {l.cellules.map((c, j) => <td key={j} className="py-2 pr-3">{c}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dash-detail-fermer-btn">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const chargerDetailKpi = (cle, setDetail, params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
  api.get(`/dashboard/detail/${cle}${qs ? `?${qs}` : ""}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e)));
};

export const Widget = ({ titre, icon: Icon, children, testId, vide }) => (
  <div className="idc-card p-4" data-testid={testId}>
    <h3 className="font-semibold text-[14px] mb-3 flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-[--idc-muted]" />} {titre}
    </h3>
    {vide ? <p className="text-[12.5px] text-[--idc-muted]">{vide}</p> : children}
  </div>
);

const MODULE_ROUTES = {
  facturation: "/facturation", stock_vehicules: "/stock-vehicules", stock_pieces: "/stock-pieces",
  planning: "/planning", dossiers: "/ventes", or: "/ordres", pipeline: "/pipeline", dashboard: "/",
};

export const Priorites = ({ items, titre = "Priorités du jour" }) => {
  const navigate = useNavigate();
  const [sel, setSel] = useState(null);
  if (!items || items.length === 0) return null;
  return (
    <div className="copilot-card px-4 py-3 mb-4" style={{ borderRadius: 12 }} data-testid="dash-priorites">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#7C5CE0]" />
        <span className="text-[11px] font-bold tracking-wide text-[#7C5CE0]">ID COPILOT</span>
        <span className="text-[13px] font-semibold">{titre}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((s) => (
          <button key={s.id} onClick={() => setSel(s)}
            className="text-left bg-white/70 rounded-lg px-3 py-2 flex items-center gap-2 hover:bg-white transition-colors"
            data-testid={`dash-priorite-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold truncate">{s.titre}</div>
              <div className="text-[11.5px] text-[--idc-muted] truncate">{s.constat}</div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-[--idc-muted] shrink-0" />
          </button>
        ))}
      </div>
      {sel && (
        <Dialog open onOpenChange={(o) => !o && setSel(null)}>
          <DialogContent className="max-w-lg" data-testid="priorite-detail-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[15px]">
                <Sparkles className="w-4 h-4 text-[#7C5CE0] shrink-0" /> {sel.titre}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="text-[11px] font-bold tracking-wide text-[--idc-muted] mb-1">CONSTAT</div>
                <p className="text-[13px]" data-testid="priorite-detail-constat">{sel.constat}</p>
              </div>
              {sel.justification && (
                <div className="rounded-lg px-3 py-2.5" style={{ background: "#F4F1FD", border: "1px solid #E3DBF8" }}>
                  <div className="text-[11px] font-bold tracking-wide text-[#7C5CE0] mb-1">POURQUOI CETTE SUGGESTION</div>
                  <p className="text-[12.5px]" data-testid="priorite-detail-justification">{sel.justification}</p>
                </div>
              )}
              {sel.payload?.message && (
                <div className="rounded-lg px-3 py-2.5 bg-[#F8FAFD] border border-[--idc-border]">
                  <div className="text-[11px] font-bold tracking-wide text-[--idc-muted] mb-1">MESSAGE PRÉPARÉ</div>
                  <p className="text-[12.5px] whitespace-pre-wrap max-h-40 overflow-y-auto">{sel.payload.message}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSel(null)} data-testid="priorite-detail-fermer-btn">Fermer</Button>
              <Button className="bg-[#16326E] hover:bg-[#122a5c]"
                onClick={() => { const dest = sel.payload?.lien || MODULE_ROUTES[sel.module] || "/"; setSel(null); navigate(dest); }}
                data-testid="priorite-detail-ouvrir-btn">
                <ExternalLink className="w-4 h-4 mr-1.5" /> {sel.action_label || "Ouvrir dans le module"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export const PeriodeSelector = ({ value, onChange }) => (
  <div className="flex bg-white border border-[--idc-border] rounded-full p-0.5" data-testid="dash-periode-selector">
    {[["jour", "Jour"], ["semaine", "Semaine"], ["mois", "Mois"]].map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)}
        className={`px-3.5 py-1 rounded-full text-[12.5px] font-medium transition-colors ${value === k ? "bg-[#16326E] text-white" : "text-[--idc-muted] hover:text-[--idc-text]"}`}
        data-testid={`dash-periode-${k}`}>{l}</button>
    ))}
  </div>
);

export const Ligne = ({ onClick, children, testId }) => (
  <button onClick={onClick} className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-[#F1F4F9] transition-colors" data-testid={testId}>
    {children}
    <ChevronRight className="w-3.5 h-3.5 text-[--idc-muted] shrink-0 ml-auto" />
  </button>
);

const LIB_PERIODE = { jour: "du jour", semaine: "de la semaine", mois: "du mois" };
const LIB_COMPARE = { jour: "vs veille", semaine: "vs sem. préc.", mois: "vs mois dernier" };

export const libPeriode = (periode, base) => `${base} ${LIB_PERIODE[periode] || "du mois"}`;

export const TendanceSub = ({ kpi, periode = "mois", extra = null }) => {
  if (!kpi) return null;
  const pct = kpi.variation_pct;
  let contenu;
  if (pct === null || pct === undefined) {
    contenu = <span className="text-[--idc-muted]">— {LIB_COMPARE[periode] || LIB_COMPARE.mois}</span>;
  } else {
    const favorable = kpi.hausse_defavorable ? pct <= 0 : pct >= 0;
    const coul = pct === 0 ? "#64748B" : favorable ? "#15803D" : "#B91C1C";
    const Icone = pct >= 0 ? TrendingUp : TrendingDown;
    contenu = (
      <span className="inline-flex items-center gap-1 font-semibold" style={{ color: coul }}>
        <Icone className="w-3 h-3 shrink-0" /> {pct > 0 ? "+" : ""}{pct} %
        <span className="font-normal text-[--idc-muted]">{LIB_COMPARE[periode] || LIB_COMPARE.mois}</span>
      </span>
    );
  }
  return <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="kpi-tendance">{contenu}{extra}</span>;
};
